import React, { Component } from "react";
import Select from "./common/select2";
import { AddCustomCut } from "./partial/addCustomCut";
import { SelectCustomerJob } from "./partial/selectCustomerJob";
import ServiceDiscountModal from "./partial/ServiceDiscountModal";
import ProductSelectionModal from "./partial/ProductSelectionModal";
import BootstrapTable from "react-bootstrap-table-next";

// UPDATED: Import the shared pricing module instead of old serviceUtils
import {
  SharedRequestPricing,
  SharedServicePricing
} from './views/services/sharedPricingCalculations';

import "react-bootstrap-table/css/react-bootstrap-table.css";
import { HTTPService } from "./../services/httpService";
import InventoryAPI from "./../services/inventoryService";
import serviceItemsService from "./../services/serviceItemsService";
import _ from "lodash";
import { toast } from "react-toastify";
import { isAreaPriced, getMarineRate, formatMarineLine, marineUnitLabel } from "./../util/marineService";
import { AREA_VAR_LABELS } from "./../util/marineCalc";
import { priceMarineLine, computeAreaLinePrices, sumRetail } from "./../util/marineLinePricing";
import { mergeCatalogs } from "./../util/catalogMerge";
import { isVehicleReadyForCatalog, isStoredVehicleReadyForCatalog } from "./../util/catalogReadiness";
import { priceMarineLineOnServer } from "./../services/marinePriceService";
import { restoreSelectedServices } from "./../util/serviceSelection";
import { MarineCalculatorModal } from "./partial/MarineCalculatorModal";
import { isMarine as isMarineAsset } from "./../util/assetType";
import { formatPrefixedProductLabel } from "./../util/productLabel";
import { formatDuration, round2 } from "./../util/durationDisplay";
import { buildDealerTemplateUrl, resolveInstallerCatalogUrl } from "./../util/serviceCatalogUrl";

const API = new HTTPService();

// Helper to map V2 response to maintain backward compatibility
const mapV2Services = (services) => {
  return services.map(service => ({
    ...service,
    // Map installer_service_id to service_id for downstream compatibility
    service_id: service.installer_service_id || service.service_id,
    // Keep original for reference if needed
    _original_installer_service_id: service.installer_service_id
  }));
};

export default class ServiceForm extends Component {
  state = {
    services: [],
    errors: {},
    selectedServices: [],
    categories: [],
    selectedService: {},
    selectedCategory: "",
    selectedDiscount: 0,
    displayServices: [],
    // UPDATED: These will be calculated using shared pricing module
    totalCost: parseFloat(this.props.total),
    totalDiscountAmount: 0,
    totalDiscounted: 0,
    totalDuration: 0,
    totalWholesale: 0,
    totalRetail: 0,
    totalMargin: 0,
    selectedServicePrice: "",
    message: "Requested Work",
    showCustom: false,
    showDisc: false,
    showServiceDiscount: false,
    serviceToDiscount: null,
    showProductSelection: false,
    serviceToSelectProduct: null,
    estimateDesc: "",
    estimateCost: 0,
    estimateDuration: 0,
    custJobs: [],
    showWholesalePricing: false,
    retailPriceOverrides: {},
    // Product selection: map of category name -> boolean (has products)
    categoryHasProducts: {},
    // Quantity support
    _nextClientId: 1,
  };

  componentDidMount() {
    // On resume, the vehicle/specialist/customerType props arrive already populated
    // at mount, so componentDidUpdate's "vehicle changed" branch never fires and the
    // Work step stays empty. Trigger that same catalog-load path once here when a
    // vehicle is already present. (Passing {} as prevProps forces the vehicle branch;
    // the serviceHint/duplicate branches are no-ops at mount — both are guarded on
    // state that is still empty.)
    if (this.props.vehicle) {
      this.componentDidUpdate({});
    }

    // ...and restore the SELECTIONS. The line above rebuilds the catalog of
    // services available to pick; this rebuilds what was already picked. The
    // picks live in this component's own state, so without it a resumed draft
    // showed an empty Work step even though the wizard held the services and
    // passed them straight back down as props.services.
    //
    // Mount only, deliberately: props.services is rewritten on every add via
    // handleServices, so seeding on update would fight the user's own edits.
    if (Array.isArray(this.props.services) && this.props.services.length > 0) {
      const { selectedServices, nextClientId } = restoreSelectedServices(
        this.props.services,
        this.state._nextClientId
      );
      this.setState({ selectedServices, _nextClientId: nextClientId }, this.calculateTotals);
    }
  }

  // Enhanced componentDidUpdate with dual pricing support (unchanged)
  async componentDidUpdate(prevProps) {
    // A customer swap — retail↔wholesale, or one dealership for another —
    // invalidates every already-selected line: each one carries the
    // PREVIOUS customer's struck price, and a dealership swap can leave a
    // line the new one's template excludes with no valid price at all.
    // Checked ahead of (and independent from) the refetch gate below so it
    // fires even if the vehicle prop happens not to change in the same
    // update (walkInForm's dealer picker never touches `vehicle`).
    const customerChanged =
      this.props.customerType !== prevProps.customerType ||
      this.props.dealerId !== prevProps.dealerId;

    if (customerChanged && this.state.selectedServices.length > 0) {
      // Clearing, not repricing. Repricing would have to re-match every line
      // back to the new catalog by service_id, silently swap a total the
      // estimator has already looked at out from under them, and produce NO
      // price at all for a line the new dealership's template restricts.
      // Clearing forces a conscious re-pick instead — the same "start the
      // Work step over" the estimator already gets from a vehicle change —
      // and it is the only option that cannot leave a plausible-but-wrong
      // number on screen.
      this.setState({ selectedServices: [], retailPriceOverrides: {} }, this.calculateTotals);
    }

    if (
      (this.props.vehicle !== prevProps.vehicle ||
        this.props.customerType !== prevProps.customerType ||
        this.props.dealerId !== prevProps.dealerId) &&
      // Read the catalog ONLY once the vehicle is fully selected, for auto and
      // marine alike. The cascade emits a new vehicle after every dropdown, so
      // this branch fired on year, then make, then model, then trim — four
      // requests with nothing to filter on, before the real one.
      //
      // Completeness rather than "enough to build a URL": a half-chosen vehicle
      // does not merely fail, it can SUCCEED and return the WRONG catalog, since
      // catalogFilterParams falls back to vehicleSize when there is no body
      // style. Reuses the same predicates that gate the Next button, so the
      // catalog can never be read for a vehicle the wizard calls unfinished.
      //
      // EDIT MODE (step 99) has no wizard: the vehicle is the stored row from
      // GET /vehicles/:vin, which need not carry body_style / id_car_trim.
      // Judged by the wizard rule it never passed, so the catalog never loaded
      // and the job picker below never opened (request 8634).
      //
      // dealerId is in this guard for the same reason customerType is:
      // walkInForm can swap one dealership for another (Previous, Previous,
      // pick a different one, Next, Next) while `vehicle` and `customerType`
      // both stay identical — without dealerId here that swap would never
      // refetch, and the job would be built against the FIRST dealership's
      // template and prices.
      (this.props.currentStep === 99
        ? isStoredVehicleReadyForCatalog(this.props.vehicle)
        : isVehicleReadyForCatalog(this.props.vehicle))
    ) {
      let allServices = [];

      if (this.props.userType === "dealer") {
        // DEALER creating job: Use /template endpoint to filter by dealer's assigned template
        const specialistId = this.props.specialist || this.props.typeId;
        const dealerId = this.props.typeId;

        const baseParams = {
          vehicleSize: this.props.vehicle.size,
          bodyStyle: this.props.vehicle.body_style,
          // A boat's size for By-size services: the estimator's pick, else the
          // model's default. An edit-mode stored row carries both; a wizard
          // vehicle carries only `size`. Ignored for a car (catalogFilterParams).
          sizeTier: this.props.vehicle.size || this.props.vehicle.size_tier,
          catalog: 'WHOLE',
          installerCategory: this.props.category,
          assetType: isMarineAsset(this.props.vehicle.vin) ? 'marine' : 'auto',
          dealerId: dealerId,
          trimId: this.props.vehicle.id_car_trim
        };

        try {
          const wholesaleUrl = buildDealerTemplateUrl(specialistId, baseParams);
          const retailUrl = buildDealerTemplateUrl(specialistId, { ...baseParams, catalog: 'RETAIL' });

          // V2 API: Handle wrapped response format
          const wholesaleResponse = await API.get(wholesaleUrl);
          const retailResponse = await API.get(retailUrl);

          // Extract data from wrapped response and map service IDs
          const wholesaleServices = mapV2Services(wholesaleResponse.data.data || wholesaleResponse.data);
          const retailServices = mapV2Services(retailResponse.data.data || retailResponse.data);

          allServices = mergeCatalogs(wholesaleServices, retailServices);
        } catch (error) {
          console.error("Error fetching dealer services:", error);
          const wholesaleUrl = buildDealerTemplateUrl(specialistId, baseParams);
          const wholesaleResponse = await API.get(wholesaleUrl);
          const wholesaleServices = mapV2Services(wholesaleResponse.data.data || wholesaleResponse.data);

          allServices = wholesaleServices.map(service => ({
            ...service,
            wholesale_price: service.service_cost,
            retail_price: service.service_cost
          }));
        }
      } else {
        // INSTALLER/SPECIALIST creating job. `resolveInstallerCatalogUrl`
        // (src/util/serviceCatalogUrl.js) is the single source of truth for
        // the routing table this covers:
        //   Wholesale (dealership) customer → /template with dealerId — the
        //     SAME resolution and membership filter the dealer's own wizard
        //     reads (server: "one path"). Without this, an installer's
        //     walk-in estimate for a dealership sent no dealer identity at
        //     all, so the same dealership could get two different wholesale
        //     prices depending on who opened the wizard, and silently
        //     ignored the dealership's service restriction too.
        //   Wholesale customer AT a mobile location → /template with BOTH
        //     dealerId and locationId — the dealer and Mobile templates are
        //     independent legs the server resolves per cell (dealer wins),
        //     so dropping locationId here would silently lose the Mobile
        //     template's surcharge/restriction for every cell the dealer's
        //     template doesn't override. See DATA_CONVENTIONS.md §4.bb.
        //   Retail customer, mobile location → /template with locationId —
        //     server applies the 'Mobile' template filter if one is defined.
        //   Retail customer, fixed location → full catalog endpoint, no
        //     template filter.
        // Catalog type depends on customer: RETAIL for retail customers, WHOLE for wholesale.
        const serviceParams = {
          vehicleSize: this.props.vehicle.size,
          bodyStyle: this.props.vehicle.body_style,
          // A boat's size for By-size services: the estimator's pick, else the
          // model's default. An edit-mode stored row carries both; a wizard
          // vehicle carries only `size`. Ignored for a car (catalogFilterParams).
          sizeTier: this.props.vehicle.size || this.props.vehicle.size_tier,
          catalog: _.upperCase(this.props.customerType),
          installerCategory: this.props.category,
          assetType: isMarineAsset(this.props.vehicle.vin) ? 'marine' : 'auto',
          trimId: this.props.vehicle.id_car_trim
        };
        const cl = this.props.currentLocation;
        const isMobileLoc = cl && cl.installer_type === 'mobile' && cl.location_id;

        const { url: servicesUrl, wholesaleIdMissing } = resolveInstallerCatalogUrl(this.props.typeId, {
          ...serviceParams,
          dealerId: this.props.dealerId,
          locationId: isMobileLoc ? cl.location_id : undefined,
        });

        if (wholesaleIdMissing) {
          // Every wholesale-customer path (walkInForm, editJob) threads
          // `dealerId` alongside customerType — this is a wiring gap, not a
          // valid state. The read still goes out (mobile-template-or-plain,
          // the pre-Task-9 route), because refusing to show ANY price blocks
          // the estimator entirely — but a dealership's negotiated price
          // book silently not applying is a money bug, not a cosmetic one,
          // so it is surfaced loudly rather than logged where only an
          // engineer would see it.
          alert(
            "This dealership's price book could not be applied — showing standard pricing instead. Please contact support before sending this estimate."
          );
          console.error(
            "[ServiceForm] Wholesale customer with no valid dealerId — falling back to the untemplated/mobile catalog read. This dealership's template was ignored.",
            { dealerId: this.props.dealerId }
          );
        }

        // V2 API: Handle wrapped response format and map service IDs
        const response = await API.get(servicesUrl);
        allServices = mapV2Services(response.data.data || response.data);
      }

      // DEBUG: Log full services data structure from database
      console.log("=== SERVICES DATA FROM DATABASE ===");
      console.log("Full services array:", JSON.parse(JSON.stringify(allServices)));
      console.log("Number of services:", allServices.length);
      if (allServices.length > 0) {
        console.log("Sample service structure:", allServices[0]);
        console.log("All service keys:", Object.keys(allServices[0]));
      }
      console.log("===================================");

      let categories = [...new Set(allServices.map((x) => x.service_category))];
      let services = [...new Set(allServices)];

      if (this.props.userType === "dealer") {
        services = services.filter(
          (service) => service.description !== "Custom"
        );
      }
        this.setState({ services, categories, selectedService: "" }, () => {
            this.handleServiceHintPreselection();
            // Load products to check which categories have products (installer only)
            if (this.props.userType === "installer") {
              this.loadCategoryProducts();
            }
        });

      if (this.props.currentStep === 99 && this.props.notes && this.props.notes.Request_id) {
        const jobsQuery = "/requestimates/requestbyID/?Id=" + this.props.notes.Request_id;
        const { data: jobs } = await API.get(jobsQuery);

        // Show job list modal - this is the workaround to populate services
        // Protection fields will be preserved via getProtectionFieldsFromProps() in addService()
        this.setState({
          custJobs: jobs,
          selectedJob: "",
          showJobList: jobs.length > 0
        });
      }
    }

    if (this.props.serviceHint !== prevProps.serviceHint && this.state.categories.length > 0) {
        this.handleServiceHintPreselection();
    }

    // Duplicate/Rebook: auto-load services from source job at current catalog prices
    if (this.props.duplicateRequestId &&
        !this._duplicateProcessed &&
        this.state.services.length > 0) {
      this._duplicateProcessed = true;
      this.loadDuplicateServices(this.props.duplicateRequestId);
    }
  }

  // Load products and build map of which categories have products
  loadCategoryProducts = async () => {
    try {
      // Get all products for this installer
      const products = await InventoryAPI.getProducts(this.props.typeId);

      // Build map of category_name -> true for categories that have products
      const categoryHasProducts = {};
      (products || []).forEach(product => {
        if (product.category_name) {
          categoryHasProducts[product.category_name] = true;
        }
      });

      this.setState({ categoryHasProducts });
    } catch (error) {
      console.error('Error loading products for categories:', error);
    }
  };

  // Service hint preselection (unchanged)
  handleServiceHintPreselection = () => {
    console.log("handleServiceHintPreselection called");
    
    if (this.props.serviceHint && 
        !this.state.selectedCategory && 
        this.state.categories.length > 0) {
        
        const serviceHintMap = {
            'PPF': 'PPF',
            'TINT': 'Glass/Tint',
            'PAINT': 'Other Services',
            'DETAIL': 'Other Services'
        };

        const suggestedCategory = serviceHintMap[this.props.serviceHint];
        
        if (suggestedCategory && this.state.categories.includes(suggestedCategory)) {
            const dispServices = this.state.services.filter(
                (service) => service.service_category === suggestedCategory
            );
            const displayServices = dispServices.map((item) => {
              if (this.props.userType === "dealer") {
                return item.description;
              }
              // Measured rows carry a per-unit RATE in service_cost, not a job
              // price — label it as a rate so it doesn't read as the total.
              const priceLabel = isAreaPriced(item)
                ? `$${(getMarineRate(item) || 0).toFixed(2)}/${marineUnitLabel(item) || "sq ft"}`
                : `$${(item.service_cost || 0).toFixed(2)}`;
              return {
                value: item.description,
                label: `${item.description} - ${priceLabel}`
              };
            });
            
            this.setState({
                displayServices,
                selectedCategory: suggestedCategory,
                selectedService: { description: "" },
                estimateCost: 0,
                estimateDuration: 0,
                estimateDesc: "",
            });
        }
    }
  };

  // Load services from a previous job (rebook/duplicate) using current catalog prices
  loadDuplicateServices = async (requestId) => {
    try {
      const { data: oldServices } = await API.get(`/requestServices/${requestId}`);

      if (!oldServices || oldServices.length === 0) {
        console.warn("No services found on source job:", requestId);
        return;
      }

      // Clear any existing selections first
      this.setState({
        selectedServices: [],
        totalCost: 0,
        totalDiscountAmount: 0,
        totalDiscounted: 0,
        totalDuration: 0
      });

      let skippedServices = [];

      for (let i = 0; i < oldServices.length; i++) {
        const oldService = oldServices[i];
        // Match to current catalog by service_id
        const catalogMatch = this.state.services.find(
          s => s.service_id === oldService.service_id
        );

        if (catalogMatch) {
          // Use catalog version but preserve quantity/unit_price from original job
          const serviceWithQty = {
            ...catalogMatch,
            quantity: oldService.quantity || 1,
            unit_price: oldService.unit_price || catalogMatch.service_cost || 0,
            allow_multiple: oldService.allow_multiple || catalogMatch.allow_multiple || 0,
          };
          this.setState({ selectedService: serviceWithQty }, () => {
            this.addService();
          });
        } else {
          skippedServices.push(oldService.custom_description || oldService.service_id);
          console.warn("Service not found in current catalog, skipping:", oldService.service_id);
        }
      }

      if (skippedServices.length > 0) {
        // Delay the alert so services finish rendering first
        setTimeout(() => {
          alert(`${skippedServices.length} service(s) from the original job are no longer in the catalog and were skipped: ${skippedServices.join(', ')}`);
        }, 500);
      }
    } catch (error) {
      console.error("Error loading duplicate services:", error);
    }
  };

  // Helper method to merge wholesale and retail pricing (unchanged)
  // Toggle pricing view for dealers (unchanged)
  togglePricingView = () => {
    this.setState(prevState => ({
      showWholesalePricing: !prevState.showWholesalePricing
    }));
  };

  // Handle retail price changes for dealers (unchanged)
  handleRetailPriceChange = (serviceId, newPrice) => {
    this.setState(prevState => {
      const updatedOverrides = {
        ...prevState.retailPriceOverrides,
        [serviceId]: newPrice
      };
      
      const updatedServices = prevState.selectedServices.map(service => {
        if (service.service_id === serviceId) {
          return {
            ...service,
            retail_price: newPrice
          };
        }
        return service;
      });
      
      return {
        retailPriceOverrides: updatedOverrides,
        selectedServices: updatedServices
      };
    }, this.calculateTotals);
  };

  // UPDATED: Use shared pricing module for all calculations
  calculateTotals = () => {
    console.log("calculateTotals called with services:", this.state.selectedServices);
    
    // Update selectedServices with current retail price overrides
    let updatedServices = this.state.selectedServices.map(service => ({
      ...service,
      retail_price: this.state.retailPriceOverrides[service.service_id] !== undefined
        ? this.state.retailPriceOverrides[service.service_id]
        : (service.retail_price || service.service_cost)
    }));
    
    if (updatedServices.length === 0) {
      this.setState({
        selectedServices: [],
        totalCost: 0,
        totalDiscountAmount: 0,
        totalDiscounted: 0,
        totalDuration: 0,
        totalWholesale: 0,
        totalRetail: 0,
        totalMargin: 0,
        hasDiscounts: false
      });
      
      this.props.handleServices([], 0, 0);
      return;
    }

    // UPDATED: Use shared pricing module for calculations
    const originalTotal = SharedRequestPricing.calculateRequestTotal(updatedServices);
    const totalDiscount = SharedRequestPricing.calculateRequestDiscount(updatedServices);
    const finalAmount = SharedRequestPricing.calculateRequestFinalAmount(updatedServices);
    const totalDuration = SharedRequestPricing.calculateTotalDuration(updatedServices);
    
    // Check if any service has a discount
    const hasDiscounts = updatedServices.some(
      service => (service.service_discount || 0) > 0
    );
    
    // Calculate dealer-specific totals if applicable
    let totalWholesale = 0;
    let totalRetail = 0;
    let totalMargin = 0;
    
    if (this.props.userType === "dealer") {
      totalWholesale = finalAmount; // What dealer pays (post-discount)
      // sumRetail, not an inline reduce: every retail_price is a LINE TOTAL,
      // including an area line's, which the calculator now makes one. The old
      // reduce was correct for unit lines by accident and totalled $8.50 for a
      // 220 sq ft marine line.
      totalRetail = sumRetail(updatedServices);
      totalMargin = totalRetail - totalWholesale;
    }
    
    // Update state with calculated values
    this.setState({
      selectedServices: updatedServices,
      totalCost: originalTotal,
      totalDiscountAmount: totalDiscount,
      totalDiscounted: finalAmount,
      totalDuration,
      totalWholesale,
      totalRetail,
      totalMargin,
      hasDiscounts,
      estimateCost: 0,
      estimateDuration: 0,
    });
    
    // UPDATED: Pass calculated values to parent using shared module results
    console.log("Passing to parent:", {
      services: updatedServices,
      total: originalTotal,
      discount: totalDiscount
    });
    
    this.props.handleServices(
      updatedServices,
      originalTotal,    // Use shared module calculation
      totalDiscount     // Use shared module calculation
    );
  };

  // Helper to get protection fields from props for a service
  // Cache for original services from props (used for protection field lookup)
  _originalServicesCache = null;

  // Flag to prevent re-running duplicate service loading
  _duplicateProcessed = false;

  getProtectionFieldsFromProps = (serviceId) => {
    if (this.props.currentStep !== 99) {
      return {};
    }

    // Use cached services if available, otherwise use current props
    // This prevents issues when props.services changes during the add loop
    const servicesToSearch = this._originalServicesCache || this.props.services;

    if (!servicesToSearch || servicesToSearch.length === 0) {
      return {};
    }

    // Find matching service by service_id
    const propsService = servicesToSearch.find(s => s.service_id === serviceId);

    if (!propsService) {
      return {};
    }
    // Return protection-related fields
    return {
      service_state: propsService.service_state,
      started_at: propsService.started_at,
      started_by: propsService.started_by,
      finished_at: propsService.finished_at,
      finished_by: propsService.finished_by,
      materials: propsService.materials,
      // Product recommendation fields
      recommended_product_id: propsService.recommended_product_id,
      recommended_product_name: propsService.recommended_product_name,
      recommended_product_brand: propsService.recommended_product_brand,
    };
  };

  // UPDATED: Simplify addService to use shared calculations
  addService = () => {
    let selectedServices = Array.from(this.state.selectedServices);

    this.setState({
      message: "NOTE : Price may vary based on selected model",
      showCustom: false,
      showMarineCalculator: false,
      marineCalcUid: null,
      marineCalcService: null,
      marineCalcReopen: false,
    });

    let goodToGo = 0;

    // Handle existing job services (editJob case)
    if (this.state.selectedService.request_id) {
      // Get protection fields from props if available
      const protectionFields = this.getProtectionFieldsFromProps(this.state.selectedService.service_id);

      const quantity = parseInt(this.state.selectedService.quantity, 10) || 1;
      const unitPrice = parseFloat(this.state.selectedService.unit_price || this.state.selectedService.service_cost || 0);
      const clientId = this.state._nextClientId;

      let serviceSelected = {
        id: this.state.selectedService.id || null,
        _clientId: clientId,
        _uid: this.state.selectedService.id || `c${clientId}`,
        description: this.state.selectedService.custom_description,
        service_category: this.state.selectedService.service_category,
        service_cost: unitPrice * quantity,
        service_discount: this.state.selectedService.service_discount || 0,
        service_amount: this.state.selectedService.service_amount,
        service_id: this.state.selectedService.service_id,
        data_collect: this.state.selectedService.data_collect,
        duration_days: this.state.selectedService.duration_days,
        installer_category: this.state.selectedService.installer_category,
        installer_id: this.state.selectedService.installer_id,
        market_size: this.state.selectedService.market_size,
        overrides: this.state.selectedService.overrides,
        vehicle_size: this.state.selectedService.vehicle_size,
        unit_of_measure: this.state.selectedService.unit_of_measure,
        pricing_kind: this.state.selectedService.pricing_kind,
        wholesale_price: this.state.selectedService.wholesale_price || this.state.selectedService.service_cost,
        retail_price: this.state.selectedService.retail_price || this.state.selectedService.service_cost,
        // The per-sq-ft RATES, frozen at creation. wholesale_price and
        // retail_price become LINE TOTALS once the area calculator confirms,
        // so the rates need their own home — and deriving prices from rates
        // rather than from the previous price is what makes re-confirming
        // idempotent.
        wholesale_rate: this.state.selectedService.wholesale_price || this.state.selectedService.service_cost,
        retail_rate: this.state.selectedService.retail_price || this.state.selectedService.service_cost,
        quantity: quantity,
        unit_price: unitPrice,
        allow_multiple: this.state.selectedService.allow_multiple || 0,
        // Product recommendation fields (from existing service or null)
        recommended_product_id: this.state.selectedService.recommended_product_id || null,
        recommended_product_name: this.state.selectedService.recommended_product_name || null,
        recommended_product_brand: this.state.selectedService.recommended_product_brand || null,
        // Merge protection fields from props (for edit mode lock icon)
        ...protectionFields,
        // Carry through task-level items for product display
        items: this.state.selectedService.items || [],
        task_count: this.state.selectedService.task_count || 0,
      };

      selectedServices.push(serviceSelected);
      this.setState({ selectedServices, _nextClientId: clientId + 1 }, this.calculateTotals);
    } else {
      // Handle new service selection
      // Marine is never "multiple units" — its quantity is area. Re-adding must
      // not increment 77 sq ft to 78; it falls through to the normal path.
      const isAllowMultiple =
        this.state.selectedService.allow_multiple === 1 &&
        !isAreaPriced(this.state.selectedService);
      // An area-priced line's quantity is CEIL(sq ft), so re-adding must never
      // increment it — but it MUST be able to add a second line, because one boat
      // can need two hull-sides lines with different areas. These are different
      // questions and `allow_multiple` used to answer only the first.
      const canAddAnotherLine =
        isAllowMultiple ||
        (isAreaPriced(this.state.selectedService) &&
          this.state.selectedService.allow_multiple === 1);
      const alreadyExists = selectedServices.find(
        (svc) => svc.service_id === this.state.selectedService.service_id
      );

      // For allow_multiple services that already exist, increment quantity
      if (alreadyExists && isAllowMultiple) {
        const updatedServices = selectedServices.map(svc => {
          if (svc.service_id === this.state.selectedService.service_id) {
            const newQty = (parseInt(svc.quantity, 10) || 1) + 1;
            const unitPrice = parseFloat(svc.unit_price || this.state.selectedService.service_cost || 0);
            return {
              ...svc,
              quantity: newQty,
              unit_price: unitPrice,
              service_cost: unitPrice * newQty,
              service_amount: unitPrice * newQty,
              duration_days: this.state.selectedService.duration_days,
              service_duration: round2((this.state.selectedService.duration_days || 0) * newQty),
            };
          }
          return svc;
        });
        this.setState({
          selectedServices: updatedServices
        }, this.calculateTotals);
      } else if (!alreadyExists || canAddAnotherLine) {
        // Handle service overrides
        if (this.state.selectedService.overrides != null) {
          const parentServices = this.state.selectedService.overrides.split(",");

          for (let x = 0; x < parentServices.length; x++) {
            let selectedServicesIDs = this.state.selectedServices.map(
              (id) => id.service_id
            );
            let foundIndex = selectedServicesIDs.indexOf(
              parseInt(parentServices[x], 10)
            );

            if (foundIndex > -1) {
              selectedServices.splice(foundIndex, 1);
            }

            this.setState({
              message: "NOTE : Price may vary based on selected model",
            });
          }
        } else {
          // Check for conflicts
          for (let x = 0; x < selectedServices.length; x++) {
            if (selectedServices[x].overrides != null) {
              const childServices = selectedServices[x].overrides.split(",");
              let found = childServices.indexOf(
                this.state.selectedService.service_id.toString()
              );

              if (found > -1) {
                goodToGo = 1;
                this.setState({
                  message: "Already included in a selected service",
                });
              }
            }
          }
        }

        if (goodToGo === 0) {
          const quantity = parseInt(this.state.selectedService.quantity, 10) || 1;
          const unitPrice = parseFloat(this.state.selectedService.unit_price || this.state.selectedService.service_cost || 0);
          const clientId = this.state._nextClientId;

          let serviceSelected = {
            id: null,
            _clientId: clientId,
            _uid: `c${clientId}`,
            description: this.state.selectedService.description,
            service_category: this.state.selectedService.service_category,
            service_cost: unitPrice * quantity,
            service_discount: 0,
            service_discount_percent: 0,
            service_amount: unitPrice * quantity,
            service_id: this.state.selectedService.service_id,
            data_collect: this.state.selectedService.data_collect,
            duration_days: this.state.selectedService.duration_days,
            installer_category: this.state.selectedService.installer_category,
            installer_id: this.state.selectedService.installer_id,
            market_size: this.state.selectedService.market_size,
            overrides: this.state.selectedService.overrides,
            vehicle_size: this.state.selectedService.vehicle_size,
            wholesale_price: this.state.selectedService.wholesale_price || this.state.selectedService.service_cost,
            retail_price: this.state.retailPriceOverrides[this.state.selectedService.service_id] !== undefined
              ? this.state.retailPriceOverrides[this.state.selectedService.service_id]
              : (this.state.selectedService.retail_price || this.state.selectedService.service_cost),
            // The per-sq-ft RATES, frozen at creation. wholesale_price and
            // retail_price become LINE TOTALS once the area calculator confirms,
            // so the rates need their own home — and deriving prices from rates
            // rather than from the previous price is what makes re-confirming
            // idempotent.
            wholesale_rate: this.state.selectedService.wholesale_price || this.state.selectedService.service_cost,
            retail_rate: this.state.selectedService.retail_price || this.state.selectedService.service_cost,
            quantity: quantity,
            unit_price: unitPrice,
            allow_multiple: this.state.selectedService.allow_multiple || 0,
            service_duration: round2((this.state.selectedService.duration_days || 0) * quantity),
            // Marine: this object is an explicit whitelist, so these must be
            // carried over deliberately. Without them the area calculator has
            // no formula and cannot price the line — vehicle_size alone is
            // enough to IDENTIFY a marine service but not to calculate one.
            asset_type: this.state.selectedService.asset_type,
            coverage_level: this.state.selectedService.coverage_level,
            area_formula: this.state.selectedService.area_formula,
            area_basis: this.state.selectedService.area_basis,
            area_factor: this.state.selectedService.area_factor,
            waste_pct: this.state.selectedService.waste_pct,
            // The unit the rate is priced PER (ft / sq_ft). Without it a linear
            // (loa_ft) line renders and totals as "sq ft". Same whitelist reason.
            unit_of_measure: this.state.selectedService.unit_of_measure,
            // Without this a Flat marine line (SF vehicle_size, pricing_kind
            // 'unit') loses its kind once it reaches the rest of the app, and
            // isAreaPriced's sentinel fallback reads the bare SF as area again.
            pricing_kind: this.state.selectedService.pricing_kind,
            // Product recommendation fields
            recommended_product_id: null,
            recommended_product_name: null,
            recommended_product_brand: null,
          };

          selectedServices.push(serviceSelected);

          // The marine branch below runs INSIDE this callback, not after the
          // setState call. handleMarineCalcConfirm maps over
          // this.state.selectedServices, so calling it before the push has
          // flushed would map the OLD array, match no _uid, and then write that
          // stale array straight back over the pending push — the line is added
          // and instantly discarded, with no error. The installer path never hit
          // this because its confirm arrives from the modal, long after flush.
          this.setState({
            selectedServices,
            _nextClientId: clientId + 1,
          }, () => {
            this.calculateTotals();

          // A marine service carries a NON-ZERO service_cost (the per-sq-ft
          // rate), so it never reaches the $0 custom-cut trigger below. Route
          // it to the area calculator instead — the line is unpriced until
          // that returns.
          if (isAreaPriced(serviceSelected)) {
            // A DEALER has nothing to decide here. Rate, formula, waste and
            // duration are all the installer's; the craft's dimensions are
            // reference data. So when the line is fully determined it is priced
            // and added directly — showing a dealer a wizard of read-only fields
            // is a click that changes nothing.
            //
            // The price stays honest without the dialog: the estimated-freeboard
            // marker rides on the line, and the estimate disclaimer under the
            // dealer total is always shown.
            //
            // A dealer is refused, never prompted, when a dimension is missing (D10).
            const auto =
              this.props.userType === "dealer"
                ? priceMarineLine(serviceSelected, this.props.vehicle)
                : null;

            if (auto && auto.ok) {
              this.handleMarineCalcConfirm({
                area: auto.area,
                rate: auto.rate,
                total: auto.total,
                overridden: false,
                variables: auto.variables,
                wastePct: auto.wastePct,
                durationDays: auto.durationDays,
                uid: serviceSelected._uid,
              });
            } else if (this.props.userType === "dealer") {
              // A dealer NEVER sees the calculator (spec 2026-09-11 D10). A
              // dimension the boat cannot supply is a reference-data gap the
              // specialist closes, not a number a dealer types.
              const dim = auto && auto.needsInput
                ? (AREA_VAR_LABELS[auto.needsInput] || auto.needsInput)
                : null;
              toast.error(
                dim
                  ? `${serviceSelected.description} needs the boat's ${dim}, which isn't on file for this model. Ask the specialist to complete the boat's dimensions.`
                  : `${serviceSelected.description} could not be priced: ${(auto && auto.error) || 'unknown error'}`
              );
              this.setState(
                (prev) => ({
                  selectedServices: (prev.selectedServices || []).filter((s) => s._uid !== serviceSelected._uid),
                }),
                this.calculateTotals
              );
            } else {
              this.setState({
                showMarineCalculator: true,
                marineCalcUid: serviceSelected._uid,
                marineCalcService: serviceSelected,
                marineCalcReopen: false,
              });
            }
          } else if (serviceSelected.service_cost === 0) {
            this.setState({ showCustom: true });
          }
          });
        }
      }
    }
  };

  // --- Marine area calculator -------------------------------------------
  // The line is written unpriced (quantity 1) and is NOT usable until the
  // calculator confirms. handleSubmit blocks on marine_area_confirmed, which
  // is what makes the rate-in-service_cost design safe.

  /**
   * Reopen the area calculator on a line that already exists.
   *
   * A persisted line does not carry `area_formula` — that lives on the CATALOG
   * row — so the line alone cannot be recalculated. Match back on the catalog
   * row and merge, letting the line win where they overlap so the rate the job
   * was priced at is preserved rather than silently reset to today's catalog
   * rate.
   *
   * MATCH THE SAME FIELD ON BOTH SIDES. `master_service_id` and `service_id`
   * are different id namespaces (`service_id` on a catalog row is the INSTALLER
   * service id — mapV2Services writes `installer_service_id` into it, and that
   * is the id the line persisted at create). A `master_service_id ?? service_id`
   * key — correct in catalogMerge, where BOTH sides are catalog rows — reads the
   * master id off the catalog and the installer id off the line here, so it
   * never matched: every saved marine line reopened with no formula and fell
   * back to manual area entry. `service_id` is the join the duplicate-job path
   * has always used for these same two shapes (`loadDuplicateServices`).
   */
  reopenMarineCalculator = (row) => {
    const catalog = this.state.services || [];
    const sameId = (a, b) => a != null && b != null && String(a) === String(b);
    const catalogRow =
      // Only when the LINE carries a master id too — same field, same namespace.
      (row?.master_service_id != null
        ? catalog.find((s) => sameId(s.master_service_id, row.master_service_id))
        : null) ||
      catalog.find((s) => sameId(s.service_id, row?.service_id)) ||
      null;
    if (!catalogRow) {
      // The modal still opens on manual entry, but that is a fallback, not the
      // intended path — say which lookup came up empty.
      console.warn(
        "[Marine] No catalog row for this line; the area calculator has no formula to auto-price with.",
        {
          line: { service_id: row?.service_id, master_service_id: row?.master_service_id },
          catalogSize: catalog.length,
          catalogIds: catalog.slice(0, 10).map((s) => ({
            service_id: s.service_id,
            master_service_id: s.master_service_id,
          })),
        }
      );
    }
    const merged = { ...(catalogRow || {}), ...row };
    // A spread copies an explicitly-undefined key over a real one, so restore
    // anything the line carries as undefined rather than not at all.
    if (catalogRow) {
      if (merged.area_formula == null) merged.area_formula = catalogRow.area_formula;
      if (merged.area_factor == null) merged.area_factor = catalogRow.area_factor;
      if (merged.waste_pct == null) merged.waste_pct = catalogRow.waste_pct;
      if (merged.unit_of_measure == null) merged.unit_of_measure = catalogRow.unit_of_measure;
    }
    this.setState({
      showMarineCalculator: true,
      marineCalcUid: row._uid,
      marineCalcService: merged,
      // Tells the dialog to seed from what this line was PRICED with rather
      // than from the boat alone, and tells cancel to leave the line alone.
      marineCalcReopen: true,
    });
  };

  /**
   * Save a MEASURED freeboard back onto the craft.
   *
   * `toVehiclePayload` carries a freeboard to the server only at estimate
   * creation, via `POST /vehicles`. But the correction actually comes later —
   * from Edit Services, after a specialist has seen the craft — and that path
   * never posts the vehicle, because the vehicle already exists. Without this
   * the reference row stays empty and the next specialist re-measures the same
   * hull, which is exactly what the learning loop exists to prevent.
   *
   * Never sends an estimate. A derived freeboard would be indistinguishable
   * from a measurement once learned, permanently, for every future boat of that
   * model.
   *
   * Best-effort: a failure here must not block a price the estimator just
   * confirmed, so it logs rather than throwing.
   */
  persistMeasuredFreeboard = async (variables) => {
    const vars = variables || {};
    if (vars.hull_height_estimated === true) return;
    const measured = Number(vars.hull_height_in);
    if (!Number.isFinite(measured) || measured <= 0) return;

    // No id means the craft does not exist yet — this is the creation flow, and
    // POST /vehicles will learn the value on submit. Sending it twice is worse
    // than not sending it here.
    const vehicleId = this.props.vehicle?.Vehicle_ID ?? this.props.vehicle?.id;
    if (!vehicleId) return;

    try {
      await API.put(`/vehicles/${vehicleId}`, { hull_height_in: measured });
    } catch (error) {
      console.error(
        '[ServiceForm] Could not save the measured freeboard to the craft; the price is unaffected.',
        error
      );
    }
  };

  /**
   * Ask the server for the authoritative figures, then apply them to the line.
   *
   * The client's number IS the billed number — nothing recomputes it after save
   * — so a locally-computed figure is an unvalidated price with no authority
   * behind it. `marineCalc` still drives the live figure the estimator watches;
   * it just stops being what gets written.
   *
   * Deliberately does NOT block on failure. A specialist who cannot add a line
   * because a pricing service is unavailable cannot work, and the endpoint
   * shares the API's availability anyway — if it is down, saving would fail too.
   * The fallback is the local figure, marked, because the server accepts a
   * client quantity without validation and a silent fallback would be billed
   * with nothing recording that it was not server-derived.
   */
  applyServerMarinePrice = async (uid, local) => {
    const line = (this.state.selectedServices || []).find((s) => s._uid === uid);
    if (!line) return;

    const installerServiceId =
      line.installer_service_id ?? line._original_installer_service_id ?? line.service_id;

    try {
      const priced = await priceMarineLineOnServer({
        installerServiceId,
        vin: this.props.vehicle?.vin,
        variables: local.variables,
        // Only when the estimator actually overrode the computed area.
        overrideSqFt: local.overridden ? local.area : undefined,
        wastePct: local.wastePct,
      });

      this.setState((prev) => ({
        selectedServices: (prev.selectedServices || []).map((svc) => {
          if (svc._uid !== uid) return svc;
          const quantity = Number(priced.quantity);
          const unitPrice = Number(priced.unit_price);
          const amount = Number(priced.service_amount);
          if (!Number.isFinite(quantity) || !Number.isFinite(amount)) return svc;
          return {
            ...svc,
            // Verbatim. Reinterpreting these would recreate the second
            // implementation this call exists to remove.
            quantity,
            unit_price: unitPrice,
            service_cost: amount,
            service_amount: amount,
            pricing_kind: priced.pricing_kind || 'area',
            price_source: 'server',
            // STORED, DELIBERATELY NOT DISPLAYED (decided 2026-09-01).
            //
            // Kept because the server returns them and they are cheap to carry,
            // not because anything renders them. Do not remove them as dead
            // weight, and do not read their absence from the UI as an oversight.
            //
            // The reasoning: the only person who could act on an override
            // comparison is whoever is doing the overriding, and the moment that
            // matters is live in the calculator, not on a saved line. A dealer
            // never overrides, so showing them "the installer entered 250 but the
            // formula says 147" invites a question they cannot evaluate.
            // `price_source: 'client'` is an audit concern rather than a display
            // one — nobody can make a pricing service come back — and the server
            // has proposed save-time validation as the durable fix.
            //
            // Revisit if end-to-end testing shows someone actually needs one.
            marine_formula_quantity: priced.formula_quantity ?? null,
            marine_overridden: priced.overridden === true,
            marine_waste_pct_source: priced.waste_pct_source || null,
            // A formula that could not run but was rescued by an override is a
            // DIFFERENT state from a number that merely disagrees with ours.
            marine_formula_error: priced.formula_error || null,
            ...computeAreaLinePrices({
              wholesaleRate: svc.wholesale_rate ?? svc.wholesale_price,
              retailRate: svc.retail_rate ?? svc.retail_price,
              area: quantity,
            }),
          };
        }),
      }), this.calculateTotals);
    } catch (error) {
      console.error(
        '[ServiceForm] Server pricing unavailable; the line keeps its locally computed figure and is marked as such.',
        error
      );
      this.setState((prev) => ({
        selectedServices: (prev.selectedServices || []).map((svc) =>
          svc._uid === uid ? { ...svc, price_source: 'client' } : svc
        ),
      }));
    }
  };

  handleMarineCalcConfirm = ({ area, rate, variables, overridden, durationDays, wastePct, uid }) => {
    // `uid` is passed explicitly by the dealer auto-add path, which prices and
    // confirms in one pass and so cannot rely on marineCalcUid having been set.
    // The modal path omits it and keeps reading state, unchanged.
    const marineCalcUid = uid ?? this.state.marineCalcUid;
    // Loud on a miss. Writing an unchanged array back is indistinguishable from
    // success and can silently undo a pending update — which is exactly how the
    // dealer auto-add path failed: the line was added and vanished, with nothing
    // logged.
    if (!this.state.selectedServices.some((svc) => svc._uid === marineCalcUid)) {
      console.error(
        '[ServiceForm] Marine confirm for a line not in state — nothing was priced.',
        { marineCalcUid, known: this.state.selectedServices.map((s) => s._uid) }
      );
      return;
    }
    const selectedServices = this.state.selectedServices.map((svc) => {
      if (svc._uid !== marineCalcUid) return svc;
      return {
        ...svc,
        quantity: area,                  // integer sq ft; roundArea guarantees >= 1
        unit_price: rate,
        // service_cost is the LINE TOTAL here, not the unit rate — that is the
        // convention addService uses (unitPrice * quantity). Leaving it at the
        // per-sq-ft rate would make calculateServiceTotal, and therefore
        // discounts and the final amount, read $8.50 for a $654.50 line.
        service_cost: Math.round(area * rate * 100) / 100,
        service_amount: Math.round(area * rate * 100) / 100,
        // Whole-job duration — never scaled by square footage. The estimator
        // sets it in the calculator (seeded from the catalog default), because a
        // bigger hull is more film, not proportionally more days.
        //
        // `duration_days` must be written too, not just service_duration:
        // sharedPricingCalculations recomputes BOTH the line duration and the
        // request total from duration_days at submission, so setting only
        // service_duration would be silently overwritten by the catalog value —
        // and the calendar is built server-side from what is stored.
        duration_days: Number(durationDays) > 0 ? Number(durationDays) : (Number(svc.duration_days) || 0),
        service_duration: Number(durationDays) > 0 ? Number(durationDays) : (Number(svc.duration_days) || 0),
        pricing_kind: 'area',
        // The two dealer price fields were frozen at the catalog RATE when the
        // line was added and were never updated here — that is why a 220 sq ft
        // line at $8.50/sq ft showed as $8.50 beside a correct wholesale total.
        // Both are LINE TOTALS from this point on, derived from the frozen rates
        // so re-confirming cannot multiply a total by the area again.
        ...computeAreaLinePrices({
          wholesaleRate: svc.wholesale_rate ?? svc.wholesale_price,
          retailRate: svc.retail_rate ?? svc.retail_price,
          area,
        }),
        marine_area_confirmed: true,
        marine_area_overridden: !!overridden,
        marine_variables: variables,
      };
    });
    this.setState({
      selectedServices,
      showMarineCalculator: false,
      marineCalcUid: null,
      marineCalcService: null,
      marineCalcReopen: false,
    }, () => {
      this.calculateTotals();
      // After the price is committed, never before: this is a data improvement
      // for future quotes, not part of pricing this one.
      this.persistMeasuredFreeboard(variables);
      // The local figure above is what the estimator just saw. This replaces it
      // with the server's, which is the one with authority behind it.
      this.applyServerMarinePrice(marineCalcUid, { area, variables, overridden, wastePct });
    });
  };

  handleMarineCalcCancel = () => {
    // Drop the unpriced line rather than leaving it to be saved at rate x 1 —
    // but ONLY a line this dialog created. The calculator is also REOPENED on a
    // line that is already priced (`reopenMarineCalculator`), and cancelling a
    // recalculation there means "leave it as it was", not "delete it": the
    // unconditional filter deleted a saved service the estimator had only
    // looked at.
    //
    // Same test handleSubmit refuses to save on, deliberately — cancel discards
    // exactly the lines that could not have been submitted anyway. A persisted
    // line carries `id`; a line confirmed earlier in this session carries
    // `marine_area_confirmed` (client-only, so both halves are needed).
    const { marineCalcUid } = this.state;
    const selectedServices = this.state.selectedServices.filter((svc) => {
      if (svc._uid !== marineCalcUid) return true;
      return Boolean(svc.id) || svc.marine_area_confirmed === true;
    });
    this.setState({
      selectedServices,
      showMarineCalculator: false,
      marineCalcUid: null,
      marineCalcService: null,
      marineCalcReopen: false,
    }, this.calculateTotals);
  };

  // Check if a service is protected from deletion (has been worked on or has materials)
  isServiceProtected = (service) => {
    const hasWorkStarted = service.service_state === 'started' || service.service_state === 'finished';
    const hasMaterials = service.materials?.length > 0;
    return hasWorkStarted || hasMaterials;
  };

  // Get protection reason for tooltip
  getProtectionReason = (service) => {
    const reasons = [];
    if (service.service_state === 'started') reasons.push('work in progress');
    if (service.service_state === 'finished') reasons.push('work completed');
    if (service.materials?.length > 0) reasons.push('materials collected');
    return reasons.length > 0 ? `Cannot remove: ${reasons.join(', ')}` : '';
  };

  // Service removal (uses _uid for unique identification)
  removeService = (row) => {
    // Check if service is protected
    if (this.isServiceProtected(row)) {
      alert(`This service cannot be removed because it has ${this.getProtectionReason(row).replace('Cannot remove: ', '')}.`);
      return;
    }

    const rowUid = row._uid || row.id || row._clientId;
    let selectedServices = Array.from(this.state.selectedServices).filter(
      (service) => (service._uid || service.id || service._clientId) !== rowUid
    );

    const retailPriceOverrides = { ...this.state.retailPriceOverrides };
    delete retailPriceOverrides[row.service_id];

    this.setState({
      selectedServices,
      retailPriceOverrides
    }, this.calculateTotals);
  };

  // Service discount handling (uses _uid for lookup)
  openServiceDiscountModal = (uid) => {
    const serviceToDiscount = this.state.selectedServices.find(
      service => service._uid === uid
    );

    if (serviceToDiscount) {
      this.setState({
        showServiceDiscount: true,
        serviceToDiscount
      });
    }
  };

  // Product selection modal handlers (uses _uid for lookup)
  openProductSelectionModal = (uid) => {
    const serviceToSelectProduct = this.state.selectedServices.find(
      service => service._uid === uid
    );

    if (serviceToSelectProduct) {
      this.setState({
        showProductSelection: true,
        serviceToSelectProduct
      });
    }
  };

  handleProductSelection = (uid, productId, productName, productBrand) => {
    // Create new array with new object for updated service (proper immutable update)
    const selectedServices = this.state.selectedServices.map(s => {
      if (s._uid === uid) {
        return {
          ...s,
          recommended_product_id: productId,
          recommended_product_name: productName,
          recommended_product_brand: productBrand
        };
      }
      return s;
    });

    this.setState({
      selectedServices,
      showProductSelection: false,
      serviceToSelectProduct: null
    }, () => {
      // Notify parent of updated services
      this.props.handleServices(
        selectedServices,
        this.state.totalCost,
        this.state.totalDiscountAmount
      );
    });
  };

  // UPDATED: Use shared pricing validation for discounts (uses _uid)
  handleServiceDiscount = (uid, discountAmount, discountPercent) => {
    const selectedServices = [...this.state.selectedServices];
    const index = selectedServices.findIndex(s => s._uid === uid);

    if (index > -1) {
      // UPDATED: Use shared pricing validation
      const validationResult = SharedServicePricing.validateServiceDiscount(
        selectedServices[index], 
        discountAmount
      );
      
      if (!validationResult.isValid) {
        console.warn("Invalid discount amount:", discountAmount, "Max:", validationResult.maxDiscount);
      }
      
      selectedServices[index].service_discount = validationResult.validatedDiscount;
      selectedServices[index].service_discount_percent = discountPercent;
      selectedServices[index].service_amount = 
        selectedServices[index].service_cost - validationResult.validatedDiscount;
      
      this.setState({ 
        selectedServices,
        showServiceDiscount: false,
        serviceToDiscount: null
      }, this.calculateTotals);
    }
  };

  // Custom estimate handling (updated)
  handleCustomEstimate = (desc, cost, duration) => {
    let selectedServices = [...this.state.selectedServices];
    let index = selectedServices.findIndex((item) => item.service_cost === 0);
    
    if (index > -1) {
      selectedServices[index].description = desc;
      selectedServices[index].service_cost = parseFloat(cost);
      selectedServices[index].duration_days = parseFloat(duration);
      selectedServices[index].service_amount = parseFloat(cost);
    }
    
    this.setState({
      selectedServices,
      estimateDesc: desc,
      estimateCost: parseFloat(cost),
      estimateDuration: parseFloat(duration),
      showCustom: false,
    }, this.calculateTotals);
  };

  // Category and service selection (unchanged)
  handleCategoryChange = (event) => {
    this.setState({
      selectedService: { description: "" },
      displayServices: [],
    });

    const { value } = event.target;

    const dispServices = this.state.services.filter(
      (service) => service.service_category === value
    );
    const displayServices = dispServices.map((item) => {
      if (this.props.userType === "dealer") {
        return item.description;
      }
      // Measured rows carry a per-unit RATE in service_cost, not a job
      // price — label it as a rate so it doesn't read as the total.
      const priceLabel = isAreaPriced(item)
        ? `$${(getMarineRate(item) || 0).toFixed(2)}/${marineUnitLabel(item) || "sq ft"}`
        : `$${(item.service_cost || 0).toFixed(2)}`;
      return {
        value: item.description,
        label: `${item.description} - ${priceLabel}`
      };
    });

    this.setState({
      displayServices,
      selectedCategory: value,
      estimateCost: 0,
      estimateDuration: 0,
      estimateDesc: "",
    });
  };

  handleServiceChange = (event) => {
    const { value } = event.target;
    const service = this.state.services.find(
      (service) =>
        service.description === value &&
        service.service_category === this.state.selectedCategory
    );
    this.setState({ selectedService: service });
  };

  // Handle inline quantity change for allow_multiple services in the table
  handleInlineQuantityChange = (uid, newQty) => {
    const qty = Math.max(1, parseInt(newQty, 10) || 1);
    const selectedServices = this.state.selectedServices.map(svc => {
      if ((svc._uid || svc.id || svc._clientId) === uid) {
        const unitPrice = parseFloat(svc.unit_price || svc.service_cost / (svc.quantity || 1) || 0);
        const discount = parseFloat(svc.service_discount || 0);
        return {
          ...svc,
          quantity: qty,
          unit_price: unitPrice,
          service_cost: unitPrice * qty,
          service_amount: (unitPrice * qty) - discount,
          service_duration: round2((svc.duration_days || 0) * qty),
        };
      }
      return svc;
    });
    this.setState({ selectedServices }, this.calculateTotals);
  };

  // Remove all discounts (updated)
  removeAllDiscounts = () => {
    let selectedServices = Array.from(this.state.selectedServices);
    
    for (let i = 0; i < selectedServices.length; i++) {
      selectedServices[i].service_discount = 0;
      selectedServices[i].service_discount_percent = 0;
      selectedServices[i].service_amount = selectedServices[i].service_cost;
    }

    this.setState({ selectedServices }, this.calculateTotals);
  };

  // Close handlers (unchanged)
  addCustomClose = () => {
    let selectedServices = [...this.state.selectedServices];
    let index = selectedServices.findIndex((item) => item.service_cost === 0);
    
    if (index > -1) {
      selectedServices.splice(index, 1);
    }
    
    this.setState({ 
      showCustom: false,
      showServiceDiscount: false,
      selectedServices 
    }, this.calculateTotals);
  };

  handleJobSelect = async (selectRequest) => {
    // Cache original services from props BEFORE the add loop
    // This is needed because props.services changes as services are added
    if (this.props.currentStep === 99 && this.props.services) {
      this._originalServicesCache = [...this.props.services];
    }

    // Load services from the selected job
    // Protection fields will be preserved via getProtectionFieldsFromProps() in addService()
    const { data: services } = await API.get(
      `/requestServices/${selectRequest.Request_id}`
    );

    // Fetch task-level items for services that have them (installer only)
    if (this.props.userType === "installer") {
      const servicesWithTasks = services.filter(s => s.task_count > 0 && s.id);
      const itemResults = await Promise.all(
        servicesWithTasks.map(s =>
          serviceItemsService.getItems(s.id, { silent: true }).then(items => ({ id: s.id, items }))
        )
      );
      const itemsMap = {};
      itemResults.forEach(r => { itemsMap[r.id] = r.items; });
      services.forEach(s => {
        if (itemsMap[s.id]) {
          s.items = itemsMap[s.id];
        }
      });
    }

    this.setState({
      selectedServices: [],
      totalCost: 0,
      totalDiscountAmount: 0,
      totalDiscounted: 0,
      totalDuration: 0,
      totalWholesale: 0,
      totalRetail: 0,
      totalMargin: 0
    });

    for (let i = 0; i < services.length; i++) {
      this.setState({ selectedService: services[i] }, () => {
        this.addService();
      });
    }

    this.setState({ showJobList: false });
  };

  handleClose = () => {
    this.setState({ 
      showJobList: false,
      showServiceDiscount: false,
      serviceToDiscount: null 
    });
  };

  render() {
    const { showWholesalePricing } = this.state;
    
    // Table columns (unchanged - keeping existing UI)
    const columns = [
      {
        dataField: "delete",
        text: "Action",
        formatter: (_, row) => {
          const isProtected = this.isServiceProtected(row);
          const protectionReason = isProtected ? this.getProtectionReason(row) : '';

          if (isProtected) {
            return (
              <i
                className="fa-sharp fa-regular fa-lock text-muted"
                style={{ cursor: "not-allowed" }}
                title={protectionReason}
              ></i>
            );
          }

          return (
            <div className="d-flex align-items-center justify-content-center">
              {/* Marine lines are the only ones whose price comes from a
                  calculation rather than a catalog lookup, so they are the only
                  ones that can be RE-calculated. Without this the only way to
                  correct a price was to delete the line and add it again —
                  which is also how a measured freeboard would have been lost. */}
              {isAreaPriced(row) && (
                <i
                  className="fa-sharp fa-regular fa-calculator text-primary mr-3"
                  style={{ cursor: "pointer" }}
                  onClick={() => this.reopenMarineCalculator(row)}
                  title="Recalculate area and price"
                ></i>
              )}
              <i
                className="fa-sharp fa-regular fa-trash text-danger"
                style={{ cursor: "pointer" }}
                onClick={() => this.removeService(row)}
                title="Remove service"
              ></i>
            </div>
          );
        },
        headerStyle: { width: "80px", textAlign: "center" },
        align: "center",
      },
      {
        dataField: "service_category",
        text: "Category",
      },
      {
        dataField: "description",
        text: "Service",
        formatter: (cell, row) => {
          const isInstaller = this.props.userType === "installer";

          // Task-level material items (from request_service_tasks)
          const taskMaterials = isInstaller && row.items
            ? row.items.filter(item => (item.type === 'material' || !item.type) && item.recommended_product_id)
            : [];

          // Service-level product (fallback when no task items)
          const hasServiceProduct = isInstaller && row.recommended_product_id != null;
          const serviceProductDisplay = hasServiceProduct
            ? formatPrefixedProductLabel(row, 'recommended_product')
            : null;

          // Only show product button if category has products defined (installer only)
          const categoryHasProducts = this.state.categoryHasProducts[row.service_category] || false;
          const showProductButton = isInstaller && categoryHasProducts;

          // Quantity display
          const qty = parseInt(row.quantity, 10) || 1;
          const unitPrice = parseFloat(row.unit_price || row.service_cost || 0);
          const isMarine = isAreaPriced(row);
          const showQty = qty > 1 || isMarine;

          // Quantity stepper, beside the service it belongs to. A marine line's
          // quantity is CEIL(sq ft) from the area calculator, not a unit count —
          // steppers would let it be nudged one square foot at a time, bypassing
          // the calculator, so they never render for marine even if the service
          // is misconfigured allow_multiple=1. Dealers do not set quantity.
          // Dealers get these too: they price multi-quantity items as well.
          // The stepper previously lived in the installer-only Final Price
          // column, which silently denied them — see the wholesale scaling
          // below, which had the same root cause.
          const showStepper = row.allow_multiple === 1 && !isMarine;
          const stepperUid = row._uid || row.id || row._clientId;

          return (
            <div className="d-flex align-items-start justify-content-between">
              <div className="flex-grow-1">
                <span>{cell}</span>
                {showQty && (
                  <span className="ml-1 text-muted small">
                    {isMarine
                      ? formatMarineLine(row)
                      : showStepper
                        ? `@ $${unitPrice.toFixed(2)}`
                        : `x${qty} @ $${unitPrice.toFixed(2)}`}
                    {isMarine && (row.marine_variables || {}).hull_height_estimated === true && (
                      // The dealer path adds this line without opening the
                      // calculator, so this is the only place the derivation is
                      // visible. Freeboard is the dimension a length rule gets
                      // most wrong — a centre console and a sedan bridge of the
                      // same length differ hugely — so an unflagged estimate
                      // reads as a measurement.
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        freeboard estimated from length
                      </div>
                    )}
                  </span>
                )}
                {taskMaterials.length > 0 ? (
                  taskMaterials.map((item, idx) => (
                    <div key={item.id || idx} className="small text-success">
                      <i className="fa-sharp fa-regular fa-check mr-1"></i>
                      {item.label}: {formatPrefixedProductLabel(item, 'recommended_product')}
                    </div>
                  ))
                ) : hasServiceProduct ? (
                  <div className="small text-success">
                    <i className="fa-sharp fa-regular fa-check mr-1"></i>
                    {serviceProductDisplay}
                  </div>
                ) : null}
              </div>
              {showStepper && (
                <div className="btn-group btn-group-sm ml-2" role="group" style={{ flexShrink: 0 }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-1"
                    onClick={() => this.handleInlineQuantityChange(stepperUid, qty - 1)}
                    disabled={qty <= 1}
                  >
                    <i className="fa-sharp fa-regular fa-minus"></i>
                  </button>
                  <span className="btn btn-light px-2" style={{ cursor: 'default', fontSize: '0.8rem' }}>
                    Qty:{qty}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-1"
                    onClick={() => this.handleInlineQuantityChange(stepperUid, qty + 1)}
                  >
                    <i className="fa-sharp fa-regular fa-plus"></i>
                  </button>
                </div>
              )}
              {showProductButton && (
                <button
                  className={`btn btn-sm ml-2 ${(taskMaterials.length > 0 || hasServiceProduct) ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                  onClick={() => this.openProductSelectionModal(row._uid)}
                  title={hasServiceProduct ? `Change product: ${serviceProductDisplay}` : 'Select product'}
                  style={{ minWidth: '32px', flexShrink: 0 }}
                >
                  <i className={`fa-sharp fa-regular ${(taskMaterials.length > 0 || hasServiceProduct) ? 'fa-check' : 'fa-cube'}`}></i>
                </button>
              )}
            </div>
          );
        }
      },
      // Pricing columns for dealers
      ...(this.props.userType === "dealer" ? [
        {
          dataField: "pricing",
          text: showWholesalePricing ? "Wholesale Pricing" : "Retail Price",
          formatter: (_, row) => {
            if (showWholesalePricing) {
              // No wholesale row for this service. Shown rather than hidden: the
              // dealer cannot be charged a retail rate, and an installer can only
              // fix what someone can see is missing.
              if (row.missing_wholesale) {
                return (
                  <span
                    className="text-danger small"
                    title="This service has no wholesale price. Ask the specialist to set one before ordering it."
                  >
                    No wholesale price
                  </span>
                );
              }
              // wholesale_price is the per-unit catalog rate, frozen when the
              // line was added, and handleInlineQuantityChange never updates it
              // — only service_cost scales. So multiply here, or a qty-3 line
              // shows 1x while the request total shows 3x.
              //
              // NEVER scale a marine line: its `quantity` is CEIL(sq ft), not a
              // unit count, and service_cost already carries rate x area.
              const wQty = isAreaPriced(row) ? 1 : (parseInt(row.quantity, 10) || 1);
              const wUnit = row.wholesale_price || null;
              const price = wUnit != null ? wUnit * wQty : row.service_cost;
              return (
                <div>
                  <span>${price.toFixed(2)}</span>
                  {wQty > 1 && wUnit != null && (
                    <small className="text-muted ml-1">
                      x{wQty} @ ${wUnit.toFixed(2)}
                    </small>
                  )}
                  {/* The derivation, under the total. This figure is what the
                      specialist invoices the dealership, so showing how it was
                      reached is what makes it defensible — and the dealer's line
                      is added without a calculator, so there is nowhere else the
                      square footage and rate appear. */}
                  {isAreaPriced(row) && (
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {formatMarineLine(row)}
                    </div>
                  )}
                </div>
              );
            } else {
              const currentRetailPrice = this.state.retailPriceOverrides[row.service_id] !== undefined 
                ? this.state.retailPriceOverrides[row.service_id]
                : (row.retail_price || row.service_cost);
              
              return (
                <div className="d-flex align-items-center">
                  <span className="mr-1">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentRetailPrice || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numericValue = value === '' ? 0 : parseFloat(value);
                      this.handleRetailPriceChange(row.service_id, numericValue);
                    }}
                    onFocus={(e) => e.target.select()}
                    className="form-control form-control-sm"
                    style={{ width: '90px' }}
                    placeholder="0.00"
                  />
                </div>
              );
            }
          }
        },
        ...(showWholesalePricing ? [{
          dataField: "margin",
          text: "Margin",
          formatter: (_, row) => {
            // Both wholesale_price and retail_price are PER UNIT and frozen at
            // add-time; only service_cost scales. Line margin must therefore be
            // per-unit margin x quantity, or it disagrees with the wholesale
            // column beside it. Identical to the old result at qty 1.
            // Marine is never scaled — its quantity is CEIL(sq ft).
            const mQty = isAreaPriced(row) ? 1 : (parseInt(row.quantity, 10) || 1);
            const wholesale = row.wholesale_price || (row.service_cost / mQty);
            const retail = this.state.retailPriceOverrides[row.service_id] !== undefined
              ? this.state.retailPriceOverrides[row.service_id]
              : (row.retail_price || (row.service_cost / mQty));
            const margin = (retail - wholesale) * mQty;
            return <span className="text-success">${margin.toFixed(2)}</span>;
          }
        }] : [])
      ] : [
        // Original columns for non-dealers
        {
          dataField: "service_cost",
          text: "Cost",
          formatter: (cell) => (
            <span>${cell.toFixed(2)}</span>
          )
        },
        {
          dataField: "service_discount",
          text: "Discount",
          formatter: (cell, row) => (
            <div>
              {cell > 0 ? (
                <>
                  <span className="text-success font-weight-bold">
                    ${(cell || 0).toFixed(2)}
                  </span>
                  {row.service_discount_percent > 0 && (
                    <span className="ml-1 text-muted">
                      ({row.service_discount_percent}%)
                    </span>
                  )}
                </>
              ) : (
                <span>$0.00</span>
              )}
              <button
                className="btn btn-sm btn-outline-primary ml-2"
                onClick={() => this.openServiceDiscountModal(row._uid)}
              >
                <i className="fa-sharp fa-regular fa-percent"></i>
              </button>
            </div>
          )
        }
      ]),
      ...(this.props.userType !== "dealer" ? [{
        dataField: "service_amount",
        text: "Final Price",
        // The quantity stepper used to live here; it now sits beside the
        // service it changes, in the Service column.
        formatter: (_, row) => {
          const finalPrice = (row.service_amount || row.service_cost - (row.service_discount || 0)).toFixed(2);
          return <span>${finalPrice}</span>;
        }
      }] : []),
      {
        dataField: "duration_days",
        text: "Duration",
        // The LINE total, like every other column on this row — Cost shows
        // $900 for 3 x $300, not the unit price. Showing the per-unit duration
        // here made a 3 x "1 hr" line total "2 hrs" and look like a bug: the
        // per-unit figure is itself rounded up (0.05 days is 0.4h, shown as
        // 1 hr), and rounding up before multiplying does not commute.
        //
        // Mirrors calculateTotalDuration's per-line branch: an area-priced
        // line's duration is the whole job and never scales by quantity.
        formatter: (_, row) => {
          const perUnit = parseFloat(row.duration_days || 0);
          const qty = isAreaPriced(row) ? 1 : (parseInt(row.quantity, 10) || 1);
          return formatDuration(round2(perUnit * qty)) ?? '-';
        },
      },
    ];

    if (this.props.currentStep !== 3 && this.props.currentStep !== 99) {
      return null;
    }

    let selectedServices = [...this.state.selectedServices];
    console.log("Available Services", selectedServices);

    return (
      <React.Fragment>
        {/* Service selection form (unchanged) */}
        <form className="form-container p-5">
          <div className="form-row justify-content-md-center">
            <div className="form-group col-md-2 text-center">
              <label htmlFor="vin" className="bold">
                Pick Service(s)
              </label>
            </div>
            <div className="form-group col-md-3">
              <Select
                name="category"
                label=" "
                options={this.state.categories}
                value={this.state.selectedCategory}
                onChange={this.handleCategoryChange}
              />
            </div>
            <div className="form-group col-md-4">
              <Select
                name="service"
                label=" "
                options={this.state.displayServices}
                value={this.state.selectedService.description}
                onChange={this.handleServiceChange}
              />
            </div>
            <div className="form-group col-md-3">
              <button
                className="jv-btn jv-btn-primary btn-block"
                type="button"
                disabled={!this.state.selectedService.description}
                onClick={this.addService}
              >
                Add Service
              </button>
            </div>
            
            {/* Modals (unchanged) */}
            <AddCustomCut
              show={this.state.showCustom}
              onHide={this.addCustomClose}
              estimateDesc={this.state.estimateDesc}
              estimateCost={this.state.estimateCost}
              estimateDuration={this.state.estimateDuration}
              handleCustomEstimate={this.handleCustomEstimate}
              addService={this.addService}
            />
            {this.state.showMarineCalculator && this.state.marineCalcService && (
              <MarineCalculatorModal
                show={this.state.showMarineCalculator}
                userType={this.props.userType}
                service={this.state.marineCalcService}
                vehicle={this.props.vehicle}
                reopen={this.state.marineCalcReopen}
                onConfirm={this.handleMarineCalcConfirm}
                onCancel={this.handleMarineCalcCancel}
              />
            )}
            {this.props.userType !== "dealer" &&
              this.state.showServiceDiscount &&
              this.state.serviceToDiscount && (
                <ServiceDiscountModal
                  show={this.state.showServiceDiscount}
                  onHide={this.handleClose}
                  service={this.state.serviceToDiscount}
                  onApplyDiscount={this.handleServiceDiscount}
                />
              )}
            {this.props.userType === "installer" &&
              this.state.showProductSelection &&
              this.state.serviceToSelectProduct && (
                <ProductSelectionModal
                  show={this.state.showProductSelection}
                  onHide={() => this.setState({ showProductSelection: false, serviceToSelectProduct: null })}
                  service={this.state.serviceToSelectProduct}
                  installerId={this.props.typeId}
                  onSelectProduct={this.handleProductSelection}
                />
              )}
          </div>
          {this.props.userType !== "dealer" &&
            this.state.selectedServices.length > 0 &&
            this.state.hasDiscounts && (
              <div className="form-row justify-content-md-center mt-3">
                <div className="form-group col-md-4">
                  <button
                    className="btn shadow btn-warning"
                    type="button"
                    onClick={this.removeAllDiscounts}
                  >
                    Remove All Discounts
                  </button>
                </div>
              </div>
            )}
        </form>

        {/* Services table and totals display (unchanged UI, updated calculations) */}
        {this.state.selectedServices.length > 0 && (
          <div>
            <div className="text-center p-5">
              <div className="d-flex align-items-center justify-content-center">
                {this.props.userType === "dealer" && (
                  <button
                    className="btn btn-sm btn-link text-muted p-1 mr-2"
                    type="button"
                    onClick={this.togglePricingView}
                    title={showWholesalePricing ? 'Switch to Retail Pricing' : 'Switch to Wholesale Pricing'}
                    style={{ fontSize: '0.9rem' }}
                  >
                    <i className={`fa-sharp fa-regular ${showWholesalePricing ? 'fa-eye' : 'fa-user'}`}></i>
                  </button>
                )}
                <h5 className="bold mb-0">{this.state.message}</h5>
              </div>
            </div>
            <div className="text-center table-responsive jv-bt-table">
              <BootstrapTable
                key={this.props.userType === "dealer" ? `dealer-${showWholesalePricing}` : "installer"}
                keyField="_uid"
                data={selectedServices}
                columns={columns}
              />
            </div>
            <div className="row">
              <div className="col text-right">
                {this.state.selectedServices && (
                  <div>
                    {this.props.userType === "dealer" ? (
                      <div>
                        <div>
                          <strong>Total:</strong> ${showWholesalePricing 
                            ? this.state.totalWholesale.toFixed(2) 
                            : this.state.totalRetail.toFixed(2)}
                        </div>
                        {showWholesalePricing && (
                          <div>
                            <strong>Total Margin:</strong> <span className="text-success">${this.state.totalMargin.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div><strong>Total Cost:</strong> ${this.state.totalCost.toFixed(2)}</div>
                        <div><strong>Final Price:</strong> ${this.state.totalDiscounted.toFixed(2)}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {this.props.userType !== "dealer" && this.state.totalDiscountAmount > 0 && (
                <div className="col text-center">
                  <strong>Total Discount:</strong> ${this.state.totalDiscountAmount.toFixed(2)}
                </div>
              )}
              <div className="col text-left">
                {this.state.selectedServices && (
                  <div><strong>Total Duration:</strong> {formatDuration(this.state.totalDuration) ?? '-'}</div>
                )}
              </div>
            </div>
          </div>
        )}
        <SelectCustomerJob
          showJobList={this.state.showJobList}
          onHide={this.handleClose}
          jobs={this.state.custJobs}
          onSelect={this.handleJobSelect}
          mode="select"
          step={this.props.currentStep}
        />
      </React.Fragment>
    );
  }
}